import yup from "@shared/lib/yupExtensions/index";
import { passwordRegex } from "@shared/utils/password/password-regex";
import type {
  SingleFieldConfig,
  ValidationRule,
  RepeaterFormFieldConfig,
  FieldArrayFormFieldConfig,
} from "../FormField/types";
import { FORM_FIELD_TYPES } from "../FormField/types";

export const validationPresets = {
  email: (t?: (key: string) => string): ValidationRule[] => [
    {
      type: "required",
      message: t?.("Email is required") || "Email is required",
    },
    {
      type: "email",
      message: t?.("Invalid email address") || "Invalid email address",
    },
  ],
  password: (t?: (key: string) => string): ValidationRule[] => [
    {
      type: "required",
      message: t?.("Password is required") || "Password is required",
    },
  ],
  strongPassword: (t?: (key: string) => string): ValidationRule[] => [
    {
      type: "required",
      message: t?.("Password is required") || "Password is required",
    },
    {
      type: "min",
      value: 7,
      message:
        t?.("Password must be at least 7 characters") ||
        "Password must be at least 7 characters",
    },
    {
      type: "matches",
      value: passwordRegex,
      message: "Password needs at least one special character",
    },
  ],
  confirmPassword: (
    matchField: string,
    t?: (key: string) => string
  ): ValidationRule[] => [
    {
      type: "required",
      message: t?.("This field is required") || "This field is required",
    },
    {
      type: "min",
      value: 7,
      message:
        t?.("Password needs to be at least 7 characters") ||
        "Password needs to be at least 7 characters",
    },
    {
      type: "matches",
      value: passwordRegex,
      message:
        t?.("Password needs to have at least one special character") ||
        "Password needs to have at least one special character",
    },
    {
      type: "oneOf",
      value: [yup.ref(matchField), null],
      message: t?.("Passwords needs to match") || "Passwords needs to match",
      ref: matchField,
    },
  ],
  phone: (t?: (key: string) => string): ValidationRule[] => [
    {
      type: "required",
      message: t?.("Phone number is required") || "Phone number is required",
    },
    {
      type: "phone",
      message: t?.("Invalid phone number") || "Invalid phone number",
    },
  ],
  requiredText: (t?: (key: string) => string): ValidationRule[] => [
    {
      type: "required",
      message: t?.("This field is required") || "This field is required",
    },
  ],
};

/**
 * Apply a single validation rule to a Yup schema
 */
function applyValidationRule(
  schema: yup.StringSchema,
  rule: ValidationRule,
  t?: (key: string) => string
): yup.StringSchema {
  const message = t && rule.message ? t(rule.message) : rule.message;

  switch (rule.type) {
    case "required":
      return schema.required(message);
    case "min":
      return schema.min(rule.value, message);
    case "max":
      return schema.max(rule.value, message);
    case "matches":
      return schema.matches(rule.value, message);
    case "email":
      return schema.email(message);
    case "oneOf":
      return rule.ref
        ? schema.oneOf([yup.ref(rule.ref), null], message)
        : schema.oneOf(rule.value, message);
    case "phone":
      return schema.phone(message);
    case "test":
      return schema.test(rule.value);
    default:
      return schema;
  }
}

function buildFieldSchema(
  config: SingleFieldConfig,
  t?: (key: string) => string
): yup.AnySchema {
  // Use number schema for number types
  if (config.type === FORM_FIELD_TYPES.NUMBER) {
    return buildNumberFieldSchema(config, t);
  }

  if (config.type === FORM_FIELD_TYPES.FILE) {
    let fileSchema = yup.mixed();

    if (config.required) {
      const message =
        typeof config.required === "string"
          ? config.required
          : t?.("This field is required") || "This field is required";
      fileSchema = fileSchema.required(message);
    }

    return fileSchema;
  }

  if (
    config.type === FORM_FIELD_TYPES.CHECKBOX ||
    config.type === FORM_FIELD_TYPES.TOGGLE
  ) {
    return yup.boolean();
  }

  let schema = yup.string();

  if (config.validationRules) {
    config.validationRules.forEach((rule) => {
      schema = applyValidationRule(schema, rule, t);
    });
  }

  if (
    config.required &&
    !config.validationRules?.some((v) => v.type === "required")
  ) {
    const message =
      typeof config.required === "string"
        ? config.required
        : t?.("This field is required") || "This field is required";
    schema = schema.required(message);
  }

  return schema;
}

/**
 * Check if a validation config is a dynamic field reference
 */
function isDynamicValidation(
  validation: unknown
): validation is { field: string; message?: string; offset?: number } {
  return (
    typeof validation === "object" &&
    validation !== null &&
    "field" in validation
  );
}

function buildNumberFieldSchema(
  config: SingleFieldConfig,
  t?: (key: string) => string
): yup.NumberSchema {
  const requiredMessage =
    typeof config.required === "string"
      ? config.required
      : t?.("This field is required") || "This field is required";

  let schema = yup
    .number()
    .transform((value, originalValue) => {
      // Handle empty strings and null as undefined
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return undefined;
      }
      return value;
    })
    .nullable();

  // Handle required - must come after transform
  if (config.required) {
    schema = schema.required(requiredMessage);
  }

  // Handle min validation
  if ("min" in config && config.min !== undefined) {
    if (isDynamicValidation(config.min)) {
      // Dynamic validation - reference another field
      const { field: refField, message, offset = 0 } = config.min;
      const defaultMessage =
        t?.(`Value must be at least ${refField}`) ||
        `Value must be at least ${refField}`;
      schema = schema.test(
        "dynamic-min",
        message || defaultMessage,
        function dynamicMinTest(value) {
          if (value === undefined || value === null) return true; // Let required handle empty
          const refValue = this.parent[refField];
          if (refValue === undefined || refValue === null) return true;
          const minValue = Number(refValue) + offset;
          return value >= minValue;
        }
      );
    } else {
      // Static validation
      const minValue =
        typeof config.min === "object" ? config.min.value : config.min;
      const minMessage =
        typeof config.min === "object"
          ? config.min.message
          : t?.(`Minimum value is ${minValue}`) ||
            `Minimum value is ${minValue}`;
      schema = schema.min(Number(minValue), minMessage);
    }
  }

  // Handle max validation
  if ("max" in config && config.max !== undefined) {
    if (isDynamicValidation(config.max)) {
      // Dynamic validation - reference another field
      const { field: refField, message, offset = 0 } = config.max;
      const defaultMessage =
        t?.(`Value must be at most ${refField}`) ||
        `Value must be at most ${refField}`;
      schema = schema.test(
        "dynamic-max",
        message || defaultMessage,
        function dynamicMaxTest(value) {
          if (value === undefined || value === null) return true; // Let required handle empty
          const refValue = this.parent[refField];
          if (refValue === undefined || refValue === null) return true;
          const maxValue = Number(refValue) + offset;
          return value <= maxValue;
        }
      );
    } else {
      // Static validation
      const maxValue =
        typeof config.max === "object" ? config.max.value : config.max;
      const maxMessage =
        typeof config.max === "object"
          ? config.max.message
          : t?.(`Maximum value is ${maxValue}`) ||
            `Maximum value is ${maxValue}`;
      schema = schema.max(Number(maxValue), maxMessage);
    }
  }

  return schema;
}

function buildRepeaterSchema(
  config: RepeaterFormFieldConfig,
  t?: (key: string) => string
): yup.ArraySchema<yup.AnySchema> {
  // Build validation schema for each item field
  const itemShape: Record<string, yup.AnySchema> = {};

  config.itemFields.forEach((itemField) => {
    // Create a SingleFieldConfig from the item field config
    const fieldConfig = {
      ...itemField.field,
      name: itemField.name,
    } as SingleFieldConfig;

    itemShape[itemField.name] = buildFieldSchema(fieldConfig, t);
  });

  let schema = yup.array().of(yup.object().shape(itemShape));

  // Handle minItems validation
  if (config.minItems && config.minItems > 0) {
    const message =
      t?.(`At least ${config.minItems} item(s) required`) ||
      `At least ${config.minItems} item(s) required`;
    schema = schema.min(config.minItems, message);
  }

  // Handle maxItems validation
  if (config.maxItems) {
    const message =
      t?.(`Maximum ${config.maxItems} item(s) allowed`) ||
      `Maximum ${config.maxItems} item(s) allowed`;
    schema = schema.max(config.maxItems, message);
  }

  // Handle required (at least 1 item)
  if (config.required) {
    const message =
      typeof config.required === "string"
        ? config.required
        : t?.("At least one item is required") ||
          "At least one item is required";
    schema = schema.min(1, message);
  }

  return schema;
}

/**
 * Build Yup array schema for FIELD_ARRAY fields
 * Creates validation for an array of objects where each object has the defined sub-fields
 */
function buildFieldArraySchema(
  config: FieldArrayFormFieldConfig,
  t?: (key: string) => string
): yup.ArraySchema<yup.AnySchema> {
  // Build schema for each item's fields
  const itemShape: Record<string, yup.AnySchema> = {};

  config.fields.forEach((field) => {
    if (!field.hidden) {
      itemShape[field.name] = buildFieldSchema(field, t);
    }
  });

  const itemSchema = yup.object().shape(itemShape);
  let arraySchema: yup.ArraySchema<yup.AnySchema> = yup.array().of(itemSchema);

  // Apply min/max constraints
  if (config.minItems !== undefined && config.minItems > 0) {
    const message =
      t?.(`At least ${config.minItems} item(s) required`) ||
      `At least ${config.minItems} item(s) required`;
    arraySchema = arraySchema.min(config.minItems, message);
  }

  if (config.maxItems !== undefined) {
    const message =
      t?.(`Maximum ${config.maxItems} items allowed`) ||
      `Maximum ${config.maxItems} items allowed`;
    arraySchema = arraySchema.max(config.maxItems, message);
  }

  return arraySchema;
}

export function createValidationSchema(
  fields: (
    | SingleFieldConfig
    | FieldArrayFormFieldConfig
    | RepeaterFormFieldConfig
  )[],
  t?: (key: string) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): yup.ObjectSchema<any> {
  const shape: Record<string, yup.AnySchema> = {};

  fields.forEach((field) => {
    if (!field.hidden) {
      // Handle REPEATER fields with nested item validation
      if (field.type === FORM_FIELD_TYPES.REPEATER) {
        shape[field.name] = buildRepeaterSchema(field, t);
        return;
      }
      // Handle FIELD_ARRAY fields
      if (field.type === FORM_FIELD_TYPES.FIELD_ARRAY) {
        shape[field.name] = buildFieldArraySchema(field, t);
        return;
      }
      shape[field.name] = buildFieldSchema(field as SingleFieldConfig, t);
    }
  });

  return yup.object().shape(shape);
}

export function isFieldRequired(config: SingleFieldConfig): boolean {
  if (config.required) return true;
  if (config.validationRules) {
    return config.validationRules.some((r) => r.type === "required");
  }
  return false;
}
